import React, { useState, useEffect } from 'react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { api } from '@/utils/api';
import { toast } from 'sonner';
import { Plus, Trash2 } from 'lucide-react';

export default function Departments() {
  const [departments, setDepartments] = useState([]);
  const [newDept, setNewDept] = useState('');
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadDepartments();
  }, []);

  const loadDepartments = async () => {
    try {
      const response = await api.getDepartments();
      setDepartments(response.data);
    } catch (error) {
      console.error('Error loading departments:', error);
      toast.error('Error al cargar departamentos');
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newDept.trim()) return;

    setLoading(true);
    try {
      await api.createDepartment({ name: newDept });
      toast.success('Departamento creado');
      setNewDept('');
      setOpen(false);
      loadDepartments();
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al crear departamento');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Está seguro de eliminar este departamento?')) return;

    try {
      await api.deleteDepartment(id);
      toast.success('Departamento eliminado');
      loadDepartments();
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al eliminar departamento');
    }
  };

  return (
    <DashboardLayout>
      <div className="max-w-4xl" data-testid="departments-page">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 uppercase tracking-tight">Departamentos</h1>
            <p className="text-gray-600 mt-1">Gestione los departamentos de la empresa</p>
          </div>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-department-button" className="bg-[#DC143C] hover:bg-[#B91C1C] text-white font-medium uppercase tracking-wide flex items-center gap-2">
                <Plus className="h-5 w-5" />
                Agregar
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle className="text-xl font-bold uppercase tracking-tight">Nuevo Departamento</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreate} className="space-y-4 mt-4">
                <div>
                  <Label htmlFor="dept_name" className="text-sm font-medium text-gray-700 mb-1.5 block uppercase tracking-wide">Nombre del Departamento</Label>
                  <Input
                    id="dept_name"
                    data-testid="department-name-input"
                    value={newDept}
                    onChange={(e) => setNewDept(e.target.value)}
                    placeholder="Ej: Recursos Humanos"
                    className="h-11"
                    required
                  />
                </div>
                <div className="flex gap-2 justify-end">
                  <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
                  <Button type="submit" data-testid="save-department-button" disabled={loading} className="bg-[#DC143C] hover:bg-[#B91C1C] text-white">
                    {loading ? 'Guardando...' : 'Guardar'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          {departments.length === 0 ? (
            <div className="p-12 text-center text-gray-500">
              <p>No hay departamentos registrados</p>
            </div>
          ) : (
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nombre</th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Fecha de Creación</th>
                  <th className="px-6 py-4 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {departments.map((dept) => (
                  <tr key={dept.id} className="hover:bg-gray-50 transition-colors" data-testid="department-row">
                    <td className="px-6 py-4 text-sm text-gray-900 font-medium">{dept.name}</td>
                    <td className="px-6 py-4 text-sm text-gray-600">{new Date(dept.created_at).toLocaleDateString('es-ES')}</td>
                    <td className="px-6 py-4 text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        data-testid="delete-department-button"
                        onClick={() => handleDelete(dept.id)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
